import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { Alert, Snackbar } from '@mui/material';

type Severity = 'success' | 'error' | 'info' | 'warning';

interface NotificationContextValue {
  notify: (message: string, severity?: Severity, durationMs?: number) => void;
  notifySuccess: (message: string, durationMs?: number) => void;
  notifyError: (message: string, durationMs?: number) => void;
}

interface NotificationState {
  open: boolean;
  message: string;
  severity: Severity;
  durationMs: number;
}

const NotificationContext = createContext<NotificationContextValue | undefined>(undefined);

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<NotificationState>({
    open: false,
    message: '',
    severity: 'info',
    durationMs: 4000,
  });

  const notify = useCallback((message: string, severity: Severity = 'info', durationMs = 4000) => {
    setState({ open: true, message, severity, durationMs });
  }, []);

  const notifySuccess = useCallback((message: string, durationMs = 4000) => {
    notify(message, 'success', durationMs);
  }, [notify]);

  const notifyError = useCallback((message: string, durationMs = 5000) => {
    notify(message, 'error', durationMs);
  }, [notify]);

  const handleClose = useCallback(() => {
    setState((prev) => ({ ...prev, open: false }));
  }, []);

  const value = useMemo(
    () => ({
      notify,
      notifySuccess,
      notifyError,
    }),
    [notify, notifySuccess, notifyError]
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <Snackbar
        open={state.open}
        autoHideDuration={state.durationMs}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleClose} severity={state.severity} variant="filled" sx={{ width: '100%' }}>
          {state.message}
        </Alert>
      </Snackbar>
    </NotificationContext.Provider>
  );
};

export const useNotification = (): NotificationContextValue => {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    throw new Error('useNotification must be used within NotificationProvider');
  }
  return ctx;
};

